import React, { useEffect, useRef } from "react"
import { Theme } from "@material-ui/core"
import { useTheme } from "@material-ui/styles"
import { CARDS_FLIPPED_EVENT } from "../util/util"

interface Bubble {
    x: number
    y: number
    z: number
    vx: number
    vy: number
    vz: number
    radius: number
}

function parseRgb(color: string) {
    const value = color.trim()
    if (value[0] === "#") {
        let hex = value.slice(1)
        if (hex.length === 3) {
            hex = hex.split("").map(ch => ch + ch).join("")
        }
        const n = parseInt(hex, 16)
        if (!Number.isNaN(n) && hex.length >= 6) {
            return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
        }
    }
    const rgb = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/)
    if (rgb) {
        return { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]) }
    }
    return { r: 180, g: 190, b: 200 }
}

function mix(a: { r: number; g: number; b: number }, b: { r: number; g: number; b: number }, t: number) {
    return {
        r: Math.round(a.r + (b.r - a.r) * t),
        g: Math.round(a.g + (b.g - a.g) * t),
        b: Math.round(a.b + (b.b - a.b) * t),
    }
}

function rgba(c: { r: number; g: number; b: number }, a: number) {
    return `rgba(${c.r}, ${c.g}, ${c.b}, ${a})`
}

function rand(min: number, max: number) {
    return min + Math.random() * (max - min)
}

export default function BubblesBackground() {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const theme = useTheme<Theme>()
    const themeColor = theme.palette.primary.main
    const isLight = theme.palette.type === "light"

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) {
            return
        }
        const ctx = canvas.getContext("2d")
        if (!ctx) {
            return
        }

        let width = 0
        let height = 0
        let raf = 0
        let running = true
        let last = performance.now()
        const tint = parseRgb(themeColor)
        const pole = isLight ? { r: 16, g: 18, b: 22 } : { r: 255, g: 255, b: 255 }
        const highlight = mix(tint, pole, isLight ? 0.58 : 0.78)
        const mid = mix(tint, pole, isLight ? 0.28 : 0.28)
        const deep = mix(tint, isLight ? { r: 22, g: 24, b: 28 } : { r: 40, g: 48, b: 56 }, isLight ? 0.62 : 0.35)
        const bubbles: Bubble[] = []

        function bubbleCount() {
            const area = Math.max(1, width * height)
            return Math.round(Math.min(48, Math.max(12, area / 48000)))
        }

        function spawnBubble(): Bubble {
            const radius = rand(42, 110)
            const pad = radius + 4
            return {
                x: rand(pad, Math.max(pad + 1, width - pad)),
                y: rand(pad, Math.max(pad + 1, height - pad)),
                z: rand(0.88, 1.12),
                vx: rand(-24, 24),
                vy: rand(-22, 22),
                vz: rand(-0.022, 0.022),
                radius,
            }
        }

        function syncBubbles() {
            const count = bubbleCount()
            while (bubbles.length < count) {
                bubbles.push(spawnBubble())
            }
            if (bubbles.length > count) {
                bubbles.length = count
            }
        }

        function resize() {
            const dpr = Math.min(window.devicePixelRatio || 1, 2)
            width = window.innerWidth
            height = window.innerHeight
            canvas!.width = Math.floor(width * dpr)
            canvas!.height = Math.floor(height * dpr)
            canvas!.style.width = `${width}px`
            canvas!.style.height = `${height}px`
            ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
            syncBubbles()
        }

        function screenRadius(b: Bubble) {
            return b.radius / b.z
        }

        function bounceWalls(b: Bubble) {
            const r = screenRadius(b)
            if (b.x - r < 0) {
                b.x = r
                b.vx = Math.abs(b.vx)
            } else if (b.x + r > width) {
                b.x = width - r
                b.vx = -Math.abs(b.vx)
            }
            if (b.y - r < 0) {
                b.y = r
                b.vy = Math.abs(b.vy)
            } else if (b.y + r > height) {
                b.y = height - r
                b.vy = -Math.abs(b.vy)
            }
            if (b.z < 0.84) {
                b.z = 0.84
                b.vz = Math.abs(b.vz)
            } else if (b.z > 1.16) {
                b.z = 1.16
                b.vz = -Math.abs(b.vz)
            }
        }

        function collide(a: Bubble, b: Bubble) {
            const dx = b.x - a.x
            const dy = b.y - a.y
            const dist = Math.hypot(dx, dy)
            const minDist = screenRadius(a) + screenRadius(b)
            if (dist >= minDist || dist < 0.0001) {
                return
            }
            const nx = dx / dist
            const ny = dy / dist
            const overlap = minDist - dist
            const ma = a.radius * a.radius
            const mb = b.radius * b.radius
            const total = ma + mb
            a.x -= nx * overlap * (mb / total)
            a.y -= ny * overlap * (mb / total)
            b.x += nx * overlap * (ma / total)
            b.y += ny * overlap * (ma / total)
            const rel = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny
            if (rel > 0) {
                return
            }
            const impulse = (2 * rel) / total
            a.vx += impulse * mb * nx
            a.vy += impulse * mb * ny
            b.vx -= impulse * ma * nx
            b.vy -= impulse * ma * ny
        }

        function drawBubble(b: Bubble) {
            const r = screenRadius(b)
            ctx!.save()
            ctx!.beginPath()
            ctx!.arc(b.x, b.y, r, 0, Math.PI * 2)
            ctx!.clip()

            const body = ctx!.createRadialGradient(
                b.x - r * 0.32,
                b.y - r * 0.38,
                r * 0.04,
                b.x + r * 0.1,
                b.y + r * 0.2,
                r
            )
            body.addColorStop(0, rgba(highlight, isLight ? 0.16 : 0.1))
            body.addColorStop(0.22, rgba(mid, isLight ? 0.09 : 0.05))
            body.addColorStop(0.68, rgba(tint, isLight ? 0.07 : 0.038))
            body.addColorStop(1, rgba(deep, isLight ? 0.12 : 0.065))
            ctx!.fillStyle = body
            ctx!.fillRect(b.x - r, b.y - r, r * 2, r * 2)

            const shade = ctx!.createRadialGradient(
                b.x + r * 0.28,
                b.y + r * 0.36,
                r * 0.1,
                b.x,
                b.y,
                r
            )
            shade.addColorStop(0, rgba(deep, isLight ? 0.08 : 0.04))
            shade.addColorStop(1, rgba(deep, 0))
            ctx!.fillStyle = shade
            ctx!.fillRect(b.x - r, b.y - r, r * 2, r * 2)

            ctx!.beginPath()
            ctx!.ellipse(b.x - r * 0.3, b.y - r * 0.34, r * 0.24, r * 0.14, -0.55, 0, Math.PI * 2)
            ctx!.fillStyle = rgba(highlight, isLight ? 0.18 : 0.11)
            ctx!.fill()

            ctx!.beginPath()
            ctx!.ellipse(b.x + r * 0.18, b.y + r * 0.28, r * 0.18, r * 0.1, 0.4, 0, Math.PI * 2)
            ctx!.fillStyle = rgba(highlight, isLight ? 0.05 : 0.028)
            ctx!.fill()

            ctx!.restore()

            ctx!.beginPath()
            ctx!.arc(b.x, b.y, r - 0.6, 0, Math.PI * 2)
            ctx!.strokeStyle = rgba(highlight, isLight ? 0.16 : 0.075)
            ctx!.lineWidth = Math.max(1, r * 0.02)
            ctx!.stroke()
        }

        function tick(now: number) {
            if (!running) {
                return
            }
            const dt = Math.min(0.033, (now - last) / 1000)
            last = now

            for (const b of bubbles) {
                b.x += b.vx * dt
                b.y += b.vy * dt
                b.z += b.vz * dt
                bounceWalls(b)
            }
            for (let i = 0; i < bubbles.length; i++) {
                for (let j = i + 1; j < bubbles.length; j++) {
                    collide(bubbles[i], bubbles[j])
                }
            }
            for (const b of bubbles) {
                bounceWalls(b)
            }

            bubbles.sort((a, b) => b.z - a.z)
            ctx!.clearRect(0, 0, width, height)
            for (const b of bubbles) {
                drawBubble(b)
            }

            raf = requestAnimationFrame(tick)
        }

        function onCardsFlipped(e: Event) {
            if ((e as CustomEvent).detail?.showingFront === false) {
                return
            }
            for (const b of bubbles) {
                b.vx += rand(-60, 60)
                b.vy += rand(-52, 52)
                b.vz += rand(-0.035, 0.035)
            }
        }

        resize()
        window.addEventListener("resize", resize)
        window.addEventListener(CARDS_FLIPPED_EVENT, onCardsFlipped)
        last = performance.now()
        raf = requestAnimationFrame(tick)

        return () => {
            running = false
            cancelAnimationFrame(raf)
            window.removeEventListener("resize", resize)
            window.removeEventListener(CARDS_FLIPPED_EVENT, onCardsFlipped)
        }
    }, [themeColor, isLight])

    return (
        <canvas
            ref={canvasRef}
            className="falling-leaves-canvas"
            aria-hidden
        />
    )
}
